/**
 * Solar Generation Data Upload Component
 * Allows users to upload hourly solar generation data
 */

import { useState, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardFooter, Button } from '../../../shared/components/ui';
import { useFileUpload } from '../../../shared/hooks/useFileUpload';
import { useBESSStore } from '../../../app/store';
import { generateSolarTemplate, validateSolarAlignment } from '../utils/solarParser';
import { apiUpload } from '../../../utils/api';
import {
  Sun,
  FileText,
  CheckCircle,
  AlertCircle,
  Download,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatNumber } from '../../../shared/utils/uploadHelpers';
import { useTranslation } from 'react-i18next';

interface SolarUploadProps {
  disabled?: boolean;
}

export function SolarUpload({ disabled = false }: SolarUploadProps) {
  const { t } = useTranslation();
  const {
    solarGeneration,
    solarConfig,
    consumption,
    setSolarGeneration,
    setProcessing,
    setError,
  } = useBESSStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const {
    files,
    isDragging,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    clearFiles,
  } = useFileUpload({
    multiple: false,
    acceptedTypes: ['.csv', '.xlsx', '.xls'],
    maxSizeMB: 50,
    onError: (error) => {
      setParseError(error);
    },
  });

  const file = files[0];

  const handleProcess = useCallback(async () => {
    if (!file) return;

    setIsProcessing(true);
    setProcessing('solar', true);
    setParseError(null);
    setWarnings([]);

    try {
      // Upload to backend for server-side parsing and storage
      const response = await apiUpload('/api/process/solar', file);
      const result = await response.json();

      if (!result.success || !result.data) {
        setParseError(result.error || t('solar.errors.unknownError'));
        setIsProcessing(false);
        setProcessing('solar', false);
        return;
      }

      // Validate alignment with consumption data if available
      if (consumption) {
        const alignment = validateSolarAlignment(
          result.data,
          consumption.startDate,
          consumption.endDate
        );

        if (!alignment.valid) {
          setParseError(alignment.warnings.join('. '));
          setIsProcessing(false);
          setProcessing('solar', false);
          return;
        }

        if (alignment.warnings.length > 0) {
          setWarnings(alignment.warnings);
        }
      }

      setSolarGeneration(result.data);
      setProcessing('solar', false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('solar.errors.processingError');
      setParseError(errorMessage);
      setError('solar', errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [file, consumption, setSolarGeneration, setProcessing, setError, t]);

  const handleDownloadTemplate = useCallback(() => {
    const blob = generateSolarTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_solar_generation.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleReset = useCallback(() => {
    setSolarGeneration(null);
    clearFiles();
    setParseError(null);
    setWarnings([]);
  }, [setSolarGeneration, clearFiles]);

  // Don't render if solar is not enabled
  if (!solarConfig.solarEnabled) {
    return null;
  }

  return (
    <Card
      variant={solarGeneration ? 'elevated' : 'default'}
      className={clsx(disabled && 'opacity-50 pointer-events-none')}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-brand-primary" />
            <h3 className="text-lg font-semibold text-swiss-charcoal">
              {t('solar.upload.title')}
            </h3>
          </div>
          {solarGeneration && (
            <CheckCircle className="w-5 h-5 text-brand-primary" />
          )}
        </div>
        <p className="text-sm text-swiss-stone mt-1">
          {t('solar.upload.subtitle')}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {!solarGeneration ? (
          <>
            {/* Upload Zone */}
            <div
              data-testid="upload-solar"
              className={clsx(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                isDragging
                  ? 'border-brand-primary bg-swiss-pearl'
                  : file
                  ? 'border-brand-primary bg-swiss-pearl'
                  : 'border-swiss-mist hover:border-brand-primary hover:bg-swiss-pearl'
              )}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                id="solar-file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                disabled={disabled}
              />
              <label htmlFor="solar-file" className="cursor-pointer space-y-2">
                <Sun className="w-8 h-8 mx-auto text-brand-primary" />
                <p className="text-sm text-swiss-charcoal">
                  {file ? file.name : t('solar.upload.dropzone')}
                </p>
                <p className="text-xs text-swiss-stone">
                  {t('solar.upload.fileHint')}
                </p>
              </label>
            </div>

            {/* Selected File Info */}
            {file && !parseError && (
              <div className="flex items-center gap-2 p-3 bg-swiss-pearl rounded-lg">
                <FileText className="w-4 h-4 text-brand-primary" />
                <span className="text-sm text-swiss-charcoal flex-1">{file.name}</span>
                <Button size="sm" variant="ghost" onClick={clearFiles}>
                  {t('solar.upload.remove')}
                </Button>
              </div>
            )}

            {/* Error Message */}
            {parseError && (
              <div className="flex items-start gap-2 p-3 bg-swiss-mist text-swiss-charcoal rounded-lg border border-swiss-stone/30">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-swiss-stone" />
                <span className="text-sm">{parseError}</span>
              </div>
            )}

            {/* Download Template */}
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              onClick={handleDownloadTemplate}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('solar.upload.downloadTemplate')}
            </Button>
          </>
        ) : (
          /* Data Summary */
          <div className="space-y-3">
            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-swiss-mist text-swiss-charcoal rounded-lg mb-3 border border-swiss-stone/30">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-swiss-stone" />
                <div className="text-sm">
                  {warnings.map((w, i) => (
                    <p key={i}>{w}</p>
                  ))}
                </div>
              </div>
            )}

            <div
              className="grid grid-cols-2 gap-3"
              data-testid="solar-data-loaded"
            >
              <div className="bg-swiss-pearl rounded-lg p-3">
                <p className="text-xs text-swiss-stone">{t('solar.data.records')}</p>
                <p className="text-lg font-semibold text-swiss-charcoal">
                  {formatNumber(solarGeneration.totalRecords)}
                </p>
              </div>

              <div className="bg-swiss-pearl rounded-lg p-3">
                <p className="text-xs text-swiss-stone">{t('solar.data.totalGeneration')}</p>
                <p className="text-lg font-semibold text-swiss-charcoal">
                  {formatNumber(solarGeneration.totalGenerationKwh, 0)} kWh
                </p>
              </div>

              <div className="bg-swiss-pearl rounded-lg p-3">
                <p className="text-xs text-swiss-stone">{t('solar.data.peakMax')}</p>
                <p className="text-lg font-semibold text-swiss-charcoal">
                  {formatNumber(solarGeneration.peakGeneration, 1)} kW
                </p>
              </div>

              <div className="bg-swiss-pearl rounded-lg p-3">
                <p className="text-xs text-swiss-stone">{t('solar.data.average')}</p>
                <p className="text-lg font-semibold text-swiss-charcoal">
                  {formatNumber(solarGeneration.avgGeneration, 1)} kW
                </p>
              </div>
            </div>

            {/* Date Range */}
            <div className="flex justify-between text-sm p-2 bg-swiss-pearl rounded-lg">
              <span className="text-swiss-stone">{t('solar.data.period')}:</span>
              <span className="font-medium text-swiss-charcoal">
                {new Date(solarGeneration.startDate).toLocaleDateString()} -{' '}
                {new Date(solarGeneration.endDate).toLocaleDateString()}
              </span>
            </div>

            {/* Capacity Factor */}
            <div className="flex justify-between text-sm">
              <span className="text-swiss-stone">{t('solar.data.capacityFactor')}:</span>
              <span className="font-medium text-swiss-charcoal">
                {solarGeneration.capacityFactor.toFixed(1)}%
              </span>
            </div>

            {/* Peak Hour */}
            {solarGeneration.statistics?.peak_hour && (
              <div className="flex justify-between text-sm">
                <span className="text-swiss-stone">{t('solar.data.peakHour')}:</span>
                <span className="font-medium text-swiss-charcoal">
                  {solarGeneration.statistics.peak_hour}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter>
        {!solarGeneration ? (
          <Button
            className="w-full"
            onClick={handleProcess}
            disabled={!file || isProcessing || disabled}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('solar.upload.processing')}
              </>
            ) : (
              t('solar.upload.processData')
            )}
          </Button>
        ) : (
          <Button className="w-full" variant="secondary" onClick={handleReset}>
            {t('solar.upload.replaceFile')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export default SolarUpload;
